import { createTheme, responsiveFontSizes } from '@mui/material/styles';

export const getAppTheme = (mode: 'light' | 'dark') => {
  const isLight = mode === 'light';

  let theme = createTheme({
    palette: {
      mode,
      primary: {
        main: '#FF3567',
        light: '#FF658E',
        dark: '#E62053',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#FF5A1F',
        light: '#FF7D4D',
        dark: '#E0450C',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#22C55E',
        light: '#86EFAC',
        dark: '#15803D',
      },
      error: {
        main: '#EF4444',
        light: '#FCA5A5',
        dark: '#B91C1C',
      },
      warning: {
        main: '#F59E0B',
        light: '#FCD34D',
        dark: '#B45309',
      },
      info: {
        main: '#3B82F6',
        light: '#93C5FD',
        dark: '#1D4ED8',
      },
      background: {
        default: isLight ? '#F9F8F4' : '#09090B',
        paper: isLight ? '#FFFFFF' : '#141417',
      },
      text: {
        primary: isLight ? '#111111' : '#F4F4F5',
        secondary: isLight ? '#71717A' : '#A1A1AA',
      },
      divider: isLight ? '#ECECEC' : 'rgba(255, 255, 255, 0.08)',
      action: {
        hover: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
        selected: isLight ? 'rgba(255, 53, 103, 0.16)' : 'rgba(255, 53, 103, 0.14)',
      },
    },
    typography: {
      fontFamily: [
        'Satoshi',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      h1: {
        fontWeight: 700,
        letterSpacing: '-0.03em',
        lineHeight: 1.1,
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.025em',
        lineHeight: 1.2,
      },
      h3: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
      },
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.015em',
        lineHeight: 1.25,
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.005em',
        lineHeight: 1.35,
      },
      subtitle1: {
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontWeight: 400,
        fontSize: '0.95rem',
        lineHeight: 1.7,
      },
      body2: {
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.01em',
        textTransform: 'none',
      },
      caption: {
        fontWeight: 500,
        lineHeight: 1.4,
      },
      overline: {
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isLight ? '#F9F8F4' : '#09090B',
            color: isLight ? '#111111' : '#F4F4F5',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '8px 18px',
            fontSize: '0.875rem',
            boxShadow: 'none',
            textTransform: 'none',
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              boxShadow: 'none',
            },
            '&.MuiButton-containedPrimary': {
              backgroundColor: '#FF3567',
              color: '#FFFFFF',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: '#E62053',
              },
            },
            '&.MuiButton-outlined': {
              borderColor: isLight ? '#D5D2C9' : 'rgba(255, 255, 255, 0.15)',
              color: isLight ? '#111111' : '#F4F4F5',
              '&:hover': {
                borderColor: isLight ? '#111111' : 'rgba(255, 255, 255, 0.3)',
                backgroundColor: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.04)',
              },
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isLight ? '#FFFFFF' : '#141417',
            borderRadius: 14,
            border: isLight ? '1px solid #ECECEC' : '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: isLight
              ? '0 2px 14px -2px rgba(0, 0, 0, 0.04)'
              : '0 4px 20px -2px rgba(0, 0, 0, 0.6)',
            transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isLight ? '#FFFFFF' : '#141417',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            backgroundImage: 'none',
            backgroundColor: isLight ? '#FFFFFF' : '#141417',
            border: isLight ? '1px solid #ECECEC' : '1px solid rgba(255, 255, 255, 0.1)',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isLight ? '#F3F2EC' : 'rgba(255, 255, 255, 0.03)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isLight ? '#E0DDD5' : 'rgba(255, 255, 255, 0.12)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isLight ? '#B5B1A5' : 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#FF3567',
              borderWidth: '1.5px',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '0.75rem',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.92rem',
            minHeight: 46,
            padding: '10px 18px',
            color: isLight ? '#555555' : '#A1A1AA',
            '&.Mui-selected': {
              color: isLight ? '#111111' : '#F4F4F5',
              fontWeight: 700,
            },
          },
        },
      },
    },
  });

  return responsiveFontSizes(theme);
};
